import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { assistencia as api, type Assistencia } from '../api/assistencia'
import { Estado } from '../componentes/Estado'
import { useContextoChatwoot } from './useContextoChatwoot'
import { BarraDeAtualizacao } from './BarraDeAtualizacao'
import { useFrescor } from './useFrescor'
import './contexto.css'

/**
 * O painel de assistência ao atendente (demanda 057 · `V38`, fatia 1).
 *
 * 🔴 **Aba própria, separada do painel de contexto** — decisão do Ferrari em
 * 12/08. Contexto é **referência**: lê-se uma vez, ao assumir a conversa.
 * Assistência é **ação**: muda a cada mensagem e é consultada durante. Misturar
 * faz o que muda se perder no que não muda, e quem paga é a leitura.
 *
 * ⭐ **Nada aqui vem de modelo.** O checklist é lido do texto do cliente por
 * código já conferido (`NormalizadorDeProduto`, `AnalisadorDeGrandeza`), o que
 * significa: sem custo de token, sem latência de provedor e **sem alucinação
 * possível**. Ele erra por omissão — nunca por invenção.
 *
 * 🔴 **E não envia nada.** Quem escreve na conversa é o atendente. Há teste no
 * servidor varrendo a ausência de verbo de escrita.
 */
export function AssistenciaConversa() {
  const { t } = useTranslation()
  const ctx = useContextoChatwoot()

  const [dados, setDados] = useState<Assistencia | null>()
  const [falhou, setFalhou] = useState(false)
  const frescor = useFrescor()

  useEffect(() => {
    if (!ctx.conversaExterna) return

    setDados(undefined)
    setFalhou(false)

    api
      .de(ctx.conversaExterna)
      .then(setDados)
      .catch(() => setFalhou(true))
      .finally(frescor.marcar)

    // `ctx.versao`: ver a nota em `ContextoConversa`. Aqui pesa ainda mais —
    // o checklist da coleta muda a cada mensagem do cliente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.conversaExterna, ctx.versao])

  if (ctx.origem === 'aguardando') return <Estado tipo="carregando" />

  if (falhou) return <Estado tipo="erro" />

  if (dados === undefined) return <Estado tipo="carregando" />

  if (!dados) return <Estado tipo="vazio" />

  return (
    <main className="contexto">
      <BarraDeAtualizacao em={frescor.em} />

      <p className="contexto__etiqueta">
        {t(`assistencia.estagio.${dados.estagio}`, { defaultValue: dados.estagio })}
      </p>

      {dados.itens.length === 0 ? (
        // 🔴 Fora de orçamento não há coleta a acompanhar. Inventar um checklist
        // para "cadê meu pedido?" seria ruído no canto da tela.
        <Estado tipo="vazio" descricao={t('assistencia.semColeta')} />
      ) : (
        <>
          <h2 className="contexto__titulo">
            {t('assistencia.titulo', { feitos: dados.coletados, total: dados.total })}
          </h2>

          <ul className="contexto__lista">
            {dados.itens.map((i) => (
              <li key={i.campo} className={i.coletado ? 'contexto__ok' : 'contexto__falta'}>
                {/* Símbolo E texto: cor não pode ser o único portador de
                    informação (`UI-09`). */}
                <span aria-hidden="true">{i.coletado ? '✓' : '○'}</span>{' '}
                <span className="contexto__rotulo">{i.rotulo}</span>
                {i.coletado ? (
                  // O literal do cliente, não o valor normalizado: é a lição do
                  // `2,5cm → 25cm`. O atendente precisa ver o que a pessoa
                  // escreveu para reconhecer o engano.
                  <strong> — {i.valor}</strong>
                ) : (
                  <span className="usuarios__secundario"> — {t('assistencia.falta')}</span>
                )}
              </li>
            ))}
          </ul>

          {dados.proximaPergunta && (
            <p className="contexto__resumo">
              <strong>{t('assistencia.proxima')}</strong> {dados.proximaPergunta}
            </p>
          )}

          {!dados.proximaPergunta && (
            <p className="contexto__resumo">{t('assistencia.completo')}</p>
          )}
        </>
      )}

      <p className="usuarios__secundario">{t('assistencia.rodape')}</p>
    </main>
  )
}
